import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const SlackApprovalHandler = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'approved' | 'rejected' | 'error'>('loading');
  const [details, setDetails] = useState<any>(null);

  // Get parameters from URL
  const action = searchParams.get('action');
  const reason = searchParams.get('reason');
  const decision = searchParams.get('decision');
  const messageTs = searchParams.get('ts');
  const userId = searchParams.get('user');
  const channel = searchParams.get('channel');

  useEffect(() => {
    const handleApproval = async () => {
      if (!decision || !messageTs) {
        setStatus('error');
        return;
      }

      try {
        // 1. Delete the original Slack message
        await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            timestamp: messageTs,
            channel: channel || 'C09H9EC5W7N'
          })
        });

        // 2. Post the final decision to Slack
        const emoji = decision === 'approve' ? '✅' : '❌';
        const verb = decision === 'approve' ? 'Approved' : 'Rejected';
        const finalMessage = `${emoji} ${verb}: ${action}\n_Decision by <@${userId}>_`;

        await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: finalMessage,
            channel: channel || 'C09H9EC5W7N'
          })
        });

        // 3. Update our state
        setStatus(decision === 'approve' ? 'approved' : 'rejected');
        setDetails({ action, reason, userId });

        // 4. Store in D1 for audit trail
        await fetch('https://futr-bet-api.b-9f2.workers.dev/approval/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action,
            reason,
            decision,
            userId,
            timestamp: new Date().toISOString()
          })
        });

      } catch (error) {
        console.error('Error processing approval:', error);
        setStatus('error');
      }
    };

    handleApproval();
  }, [decision, messageTs, action, reason, userId, channel]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white flex items-center justify-center">
      <div className="max-w-2xl w-full mx-auto p-8">
        {status === 'loading' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto mb-4"></div>
            <p className="text-gray-400">Processing your decision...</p>
          </div>
        )}

        {status === 'approved' && (
          <div className="bg-green-900/30 border border-green-500 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-3xl font-bold mb-2">Approved!</h1>
            <p className="text-gray-300 mb-4">You approved: {action}</p>
            <p className="text-sm text-gray-400">Claude is proceeding with the action.</p>
            <div className="mt-6 pt-6 border-t border-green-800">
              <p className="text-xs text-gray-500">This window can be closed</p>
            </div>
          </div>
        )}

        {status === 'rejected' && (
          <div className="bg-red-900/30 border border-red-500 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-3xl font-bold mb-2">Rejected</h1>
            <p className="text-gray-300 mb-4">You rejected: {action}</p>
            <p className="text-sm text-gray-400">Claude has cancelled the action.</p>
            <div className="mt-6 pt-6 border-t border-red-800">
              <p className="text-xs text-gray-500">This window can be closed</p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-3xl font-bold mb-2">Error</h1>
            <p className="text-gray-300">Something went wrong processing your decision.</p>
            <p className="text-sm text-gray-500 mt-4">Please try again or check Slack.</p>
          </div>
        )}

        {/* Show raw data for debugging */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 p-4 bg-gray-800 rounded text-xs font-mono">
            <p>Action: {action}</p>
            <p>Reason: {reason}</p>
            <p>Decision: {decision}</p>
            <p>Message TS: {messageTs}</p>
            <p>User: {userId}</p>
            <p>Channel: {channel}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlackApprovalHandler;